import { useState, useEffect } from 'react';
import { AlertTriangle, ExternalLink, CheckCircle, Database } from 'lucide-react';
import { isSupabaseConfigured } from '../lib/supabase';

export function DeploymentStatus() {
  const [backendStatus, setBackendStatus] = useState('checking');
  const [showDetails, setShowDetails] = useState(false);
  const supabaseConfigured = isSupabaseConfigured();

  useEffect(() => {
    checkBackendStatus();
  }, []);

  const checkBackendStatus = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch('https://pixivloader.onrender.com/health', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        setBackendStatus('online');
      } else {
        setBackendStatus('offline');
      }
    } catch (error) {
      setBackendStatus('offline');
    }
  };

  if (supabaseConfigured) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        background: '#d1fae5',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#065f46',
        marginBottom: '16px'
      }}>
        <Database size={16} />
        <span>Supabase authentication is configured and ready!</span>
      </div>
    );
  }

  if (backendStatus === 'online') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        background: '#d1fae5',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#065f46',
        marginBottom: '16px'
      }}>
        <CheckCircle size={16} />
        <span>Backend server is online (fallback authentication)</span>
      </div>
    );
  }

  return (
    <div style={{
      padding: '16px',
      background: '#fef3c7',
      borderRadius: '12px',
      marginBottom: '24px',
      border: '1px solid #f59e0b'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
      }}>
        <AlertTriangle size={20} color="#f59e0b" />
        <h3 style={{ margin: 0, color: '#92400e', fontSize: '16px', fontWeight: '600' }}>
          Authentication Not Configured
        </h3>
      </div>
      
      <p style={{ margin: '0 0 12px 0', color: '#92400e', fontSize: '14px' }}>
        Set up Supabase for secure authentication or use the fallback backend server.
      </p>
      
      <div style={{ marginBottom: '12px' }}>
        <strong style={{ color: '#92400e', fontSize: '14px' }}>Recommended: Supabase Setup</strong>
        <ul style={{ margin: '8px 0', paddingLeft: '20px', color: '#92400e', fontSize: '14px' }}>
          <li>Secure authentication with email verification</li>
          <li>Production-ready user management</li>
          <li>Free tier available</li>
        </ul>
      </div>
      
      <button
        onClick={() => setShowDetails(!showDetails)}
        style={{
          background: '#f59e0b',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '14px',
          cursor: 'pointer',
          marginRight: '8px'
        }}
      >
        {showDetails ? 'Hide' : 'Show'} Setup Guide
      </button>
      
      <button
        onClick={checkBackendStatus}
        style={{
          background: 'transparent',
          color: '#f59e0b',
          border: '1px solid #f59e0b',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '14px',
          cursor: 'pointer'
        }}
      >
        Check Again
      </button>
      
      {showDetails && (
        <div style={{
          marginTop: '16px',
          padding: '16px',
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#92400e'
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>
            Option 1: Supabase Setup (Recommended):
          </h4>
          <ol style={{ margin: '0 0 16px 0', paddingLeft: '20px' }}>
            <li>Go to <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" style={{ color: '#f59e0b' }}>supabase.com</a></li>
            <li>Create new project (2 minutes)</li>
            <li>Copy Project URL and API key</li>
            <li>Add to environment variables</li>
            <li>Run SQL setup script</li>
          </ol>
          
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>
            Option 2: Backend Server:
          </h4>
          <ol style={{ margin: 0, paddingLeft: '20px' }}>
            <li>Deploy backend to <a href="https://dashboard.render.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#f59e0b' }}>Render</a></li>
            <li>Connect GitHub repo: <code>gitchking/Pixivloader</code></li>
            <li>Configure environment variables</li>
          </ol>
          
          <div style={{ marginTop: '12px' }}>
            <a 
              href="https://github.com/gitchking/Pixivloader/blob/master/SUPABASE_QUICK_SETUP.md" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                color: '#f59e0b',
                textDecoration: 'none',
                fontSize: '14px',
                marginRight: '12px'
              }}
            >
              <Database size={14} />
              Supabase Setup Guide
            </a>
            <a 
              href="https://github.com/gitchking/Pixivloader/blob/master/COMPLETE_DEPLOYMENT.md" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                color: '#f59e0b',
                textDecoration: 'none',
                fontSize: '14px'
              }}
            >
              <ExternalLink size={14} />
              Backend Deployment
            </a>
          </div>
        </div>
      )}
    </div>
  );
}